import React from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { billingService, SubscriptionTier } from '../../services/Billing/billingService';
import { useSubscriptionContext } from '../../providers/SubscriptionProvider';
import {
  FREE_FEATURES,
  PREMIUM_FEATURES,
  PREMIUM_PLUS_FEATURES,
  PLAN_TAGLINES,
  planCtaState,
  planCtaLabel,
  planCtaEnabled,
  showsIntroPrice,
} from '../../config/planFeatures';

interface PremiumModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ModalBackdrop = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bg-primary);
  backdrop-filter: blur(10px);
  z-index: 11000;
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  flex-direction: column;
  overflow-y: auto;
  padding: ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.md};
  }
`;

const ModalContent = styled.div`
  position: relative;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  color: var(--text-primary);
  padding: ${theme.spacing.xl} 0;
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${theme.spacing.md};
  right: ${theme.spacing.md};
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.full};
  background: var(--bg-secondary);
  border: 1px solid var(--border-light);
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 300;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 11001;
  transition: all 0.2s ease;
  line-height: 1;
  padding: 0;

  &:hover {
    background: var(--bg-tertiary);
    border-color: var(--border-medium);
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 36px;
    height: 36px;
    font-size: 1.3rem;
    top: ${theme.spacing.sm};
    right: ${theme.spacing.sm};
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing.xl};
`;

const CrownIcon = styled.div`
  font-size: 4rem;
  margin-bottom: ${theme.spacing.md};
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  filter: drop-shadow(0 0 10px rgba(255, 215, 0, 0.5));
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: ${theme.spacing.sm};
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary);
  margin-bottom: ${theme.spacing.xl};

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1rem;
  }
`;

const PackagesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};
`;

const PackageCard = styled.div<{ $isPopular?: boolean }>`
  background: ${props => props.$isPopular
    ? 'linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(255, 165, 0, 0.15) 100%)'
    : 'var(--bg-card)'};
  border: 2px solid ${props => props.$isPopular
    ? 'rgba(255, 215, 0, 0.5)'
    : 'var(--border-light)'};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  position: relative;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-4px);
    border-color: ${props => props.$isPopular
    ? 'rgba(255, 215, 0, 0.8)'
    : 'var(--border-medium)'};
    box-shadow: 0 8px 24px rgba(255, 215, 0, ${props => props.$isPopular ? '0.3' : '0.15'});
  }

  ${props => props.$isPopular && `
    &::before {
      content: 'MOST POPULAR';
      position: absolute;
      top: -12px;
      left: 50%;
      transform: translateX(-50%);
      background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
      color: var(--text-inverse);
      padding: 4px 16px;
      border-radius: 20px;
      font-size: 0.75rem;
      font-weight: 700;
      letter-spacing: 0.5px;
    }
  `}
`;

const PackageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${theme.spacing.md};
`;

const PackageName = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: ${theme.spacing.xs};
  color: var(--text-primary);

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.3rem;
  }
`;

const PackagePrice = styled.div`
  text-align: right;
`;

const PriceAmount = styled.div`
  font-size: 2rem;
  font-weight: 700;
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.5rem;
  }
`;

const PricePeriod = styled.div`
  font-size: 0.9rem;
  color: var(--text-secondary);
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: ${theme.spacing.md} 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.sm};
  font-size: 1rem;
  color: var(--text-primary);

  &::before {
    content: '✓';
    color: #FFD700;
    font-weight: 700;
    font-size: 1.2rem;
    flex-shrink: 0;
  }
`;

const SubscribeButton = styled.button<{ $isPopular?: boolean }>`
  width: 100%;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  background: ${props => props.$isPopular
    ? 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)'
    : 'var(--bg-secondary)'};
  border: 2px solid ${props => props.$isPopular
    ? 'transparent'
    : 'var(--border-medium)'};
  border-radius: ${theme.borderRadius.md};
  color: ${props => props.$isPopular ? '#000' : 'var(--text-primary)'};
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: ${theme.spacing.md};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    background: ${props => props.$isPopular
    ? 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)'
    : 'var(--bg-tertiary)'};
    box-shadow: 0 4px 12px ${props => props.$isPopular
    ? 'rgba(255, 215, 0, 0.4)'
    : 'rgba(0, 0, 0, 0.15)'};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const Footer = styled.div`
  text-align: center;
  margin-top: ${theme.spacing.xl};
  padding-top: ${theme.spacing.lg};
  border-top: 1px solid var(--border-light);
  color: var(--text-secondary);
  font-size: 0.9rem;
`;

const FooterText = styled.p`
  margin: 0;

  &:not(:first-child) {
    margin-top: ${theme.spacing.sm};
  }
`;

const RegularPrice = styled.div`
  font-size: 0.85rem;
  color: #999;
  text-decoration: line-through;
  margin-bottom: 2px;
`;

// The intro price runs for a fixed number of billing cycles and then reverts.
// Stating that next to the price is not optional decoration — showing "$5.99"
// alone and then charging $9.99 in month three is what produces disputes.
const IntroTerms = styled.div`
  font-size: 0.75rem;
  color: var(--text-secondary);
  margin-top: 4px;
  line-height: 1.3;
  max-width: 12rem;
  text-align: right;
`;

const PackageTagline = styled.div`
  font-size: 0.8rem;
  color: var(--text-secondary);
  opacity: 0.85;
  margin-top: 2px;
  max-width: 14rem;
`;

// Shown because "20 swipes a day" only means something next to the 10 you get
// without paying. Hiding the free tier makes every number unreadable.
const FreePlanPanel = styled.div`
  margin-top: ${theme.spacing.lg};
  padding: ${theme.spacing.md};
  border-radius: 12px;
  border: 1px dashed rgba(127, 127, 127, 0.35);
`;

const FreePlanTitle = styled.h4`
  margin: 0 0 ${theme.spacing.sm} 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-secondary);
`;

const FreePlanList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.25rem 1rem;
`;

const FreePlanItem = styled.li`
  font-size: 0.82rem;
  color: var(--text-secondary);
  opacity: 0.9;
  padding-left: 1rem;
  position: relative;

  &::before {
    content: '·';
    position: absolute;
    left: 0.25rem;
  }
`;

const CurrentBadge = styled.span`
  display: inline-block;
  margin-left: 0.5rem;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  vertical-align: middle;
  background: rgba(255, 140, 66, 0.18);
  color: #ff8c42;
`;

const StatusBanner = styled.div`
  margin-bottom: ${theme.spacing.md};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: 12px;
  border: 1px solid rgba(255, 140, 66, 0.35);
  background: rgba(255, 140, 66, 0.08);
  font-size: 0.85rem;
  line-height: 1.4;
  text-align: center;
`;

const ErrorText = styled.div`
  color: ${theme.colors.error};
  font-size: 0.9rem;
  text-align: center;
  margin-bottom: ${theme.spacing.md};
`;

interface Plan {
  id: SubscriptionTier;
  name: string;
  /** Introductory price, charged for `introMonths` billing cycles. */
  price: string;
  /** Full price, charged once the intro period ends. */
  regularPrice: string;
  /** Length of the intro discount, matching the Stripe coupon's duration. */
  introMonths: number;
  features: string[];
  /** One line on what separates this tier from the one below. */
  tagline: string;
  isPopular?: boolean;
}

// Feature lists come from config/planFeatures, which is also what any other
// surface describing the plans reads. Every line there is a limit enforced
// server-side — nothing unbuilt is advertised here any more.
const PLANS: Plan[] = [
  {
    id: 'premium',
    name: 'Premium',
    price: '$5.99',
    regularPrice: '$9.99',
    introMonths: 2,
    tagline: PLAN_TAGLINES.premium,
    features: PREMIUM_FEATURES,
  },
  {
    id: 'premium_plus',
    name: 'Premium Plus',
    price: '$14.99',
    regularPrice: '$19.99',
    introMonths: 2,
    isPopular: true,
    tagline: PLAN_TAGLINES.premium_plus,
    features: PREMIUM_PLUS_FEATURES,
  }
];

export const PremiumModal: React.FC<PremiumModalProps> = ({
  isOpen,
  onClose
}) => {
  const [processingPlanId, setProcessingPlanId] = React.useState<SubscriptionTier | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  // A plan change made in place has no redirect to show for it, so it needs
  // somewhere to be reported.
  const [notice, setNotice] = React.useState<string | null>(null);
  // The live plan, shared with the rest of the app. The modal used to show
  // the same two "Start 60-Day Trial" cards to everyone — including someone
  // already paying for the exact plan the button offered to sell them.
  const {
    subscription, tier, isPremium, cancelAtPeriodEnd, refresh, refreshAfterBillingChange,
  } = useSubscriptionContext();
  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (isOpen) {
      setProcessingPlanId(null);
      setError(null);
      // Opened right after subscribing or cancelling elsewhere, this is the
      // surface where a stale plan is most visible.
      refresh();
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, refresh]);

  // Close on Escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleSelectPlan = async (plan: Plan) => {
    setError(null);
    setNotice(null);
    setProcessingPlanId(plan.id);
    try {
      const result = await billingService.createCheckoutSession(plan.id);

      if (result.url) {
        window.location.href = result.url;
        return;
      }

      // No URL means there was nothing to buy: the account already had a
      // subscription, so the server moved it to this plan instead of selling a
      // second one. Report it here rather than redirecting into a checkout
      // that would have created a duplicate.
      refreshAfterBillingChange();
      const on = result.effectiveAt
        ? new Date(result.effectiveAt).toLocaleDateString(undefined,
          { year: 'numeric', month: 'long', day: 'numeric' })
        : null;
      setNotice(
        result.effective === 'now'
          ? `You're on ${plan.name} now.`
          : on
            ? `You'll move to ${plan.name} on ${on}. Nothing changes until then.`
            : `You'll move to ${plan.name} at the end of this billing period.`,
      );
      setProcessingPlanId(null);
    } catch (err: any) {
      setError(err.message || 'Something went wrong starting checkout. Please try again.');
      setProcessingPlanId(null);
    }
  };

  // What this account can do with each plan card. Written once here rather
  // than inline, because "which button does this person get" is the whole
  // question the modal now answers.
  const planState = (plan: Plan) => planCtaState(plan.id, tier, isPremium);

  const ctaLabel = (plan: Plan) => planCtaLabel(planState(plan), plan.name, {
    processing: processingPlanId === plan.id,
    cancelAtPeriodEnd,
  });

  const periodEnd = subscription.currentPeriodEnd
    ? new Date(subscription.currentPeriodEnd).toLocaleDateString(undefined,
        { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <ModalBackdrop $isOpen={isOpen} onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton
          onClick={onClose}
          title="Close"
          aria-label="Close premium modal"
        >
          ×
        </CloseButton>

        <Header>
          <CrownIcon>👑</CrownIcon>
          <Title>{isPremium ? 'Your plan' : 'Choose your plan'}</Title>
          <Subtitle>
            {isPremium
              ? `You are on ${tier === 'premium_plus' ? 'Premium Plus' : 'Premium'}. Everything below is exactly what each plan unlocks.`
              : 'Every plan below lists exactly what it unlocks — no more, no less'}
          </Subtitle>
        </Header>

        {error && <ErrorText>{error}</ErrorText>}
        {notice && <StatusBanner>{notice}</StatusBanner>}

        {isPremium && cancelAtPeriodEnd && (
          <StatusBanner>
            Your subscription is set to end{periodEnd ? ` on ${periodEnd}` : ' at the end of this period'}.
            You keep everything below until then — you can reactivate from the profile menu.
          </StatusBanner>
        )}

        <PackagesContainer>
          {PLANS.map((plan) => (
            <PackageCard key={plan.id} $isPopular={plan.isPopular && showsIntroPrice(planState(plan))}>
              <PackageHeader>
                <div>
                  <PackageName>
                    {plan.name}
                    {planState(plan) === 'current' && <CurrentBadge>Current</CurrentBadge>}
                  </PackageName>
                  <PackageTagline>{plan.tagline}</PackageTagline>
                </div>
                <PackagePrice>
                  {showsIntroPrice(planState(plan)) ? (
                    <>
                      <RegularPrice>{plan.regularPrice}/mo</RegularPrice>
                      <PriceAmount>{plan.price}</PriceAmount>
                      <PricePeriod>/month</PricePeriod>
                      <IntroTerms>
                        for your first {plan.introMonths} months,
                        then {plan.regularPrice}/month
                      </IntroTerms>
                    </>
                  ) : (
                    // No intro price for someone who already subscribed. The
                    // discount is a one-time coupon on the Stripe side, so
                    // promising it again on an upgrade would be a price this
                    // checkout may not honour.
                    <>
                      <PriceAmount>{plan.regularPrice}</PriceAmount>
                      <PricePeriod>/month</PricePeriod>
                    </>
                  )}
                </PackagePrice>
              </PackageHeader>
              <FeaturesList>
                {plan.features.map((feature, index) => (
                  <FeatureItem key={index}>{feature}</FeatureItem>
                ))}
              </FeaturesList>
              <SubscribeButton
                $isPopular={plan.isPopular && planState(plan) !== 'current'}
                onClick={() => handleSelectPlan(plan)}
                disabled={processingPlanId !== null || !planCtaEnabled(planState(plan))}
              >
                {ctaLabel(plan)}
              </SubscribeButton>
            </PackageCard>
          ))}
        </PackagesContainer>

        {!isPremium && (
        <FreePlanPanel>
          <FreePlanTitle>What you already have on Free</FreePlanTitle>
          <FreePlanList>
            {FREE_FEATURES.map((feature, index) => (
              <FreePlanItem key={index}>{feature}</FreePlanItem>
            ))}
          </FreePlanList>
        </FreePlanPanel>
        )}

        <Footer>
          <FooterText>Cancel anytime. All subscriptions auto-renew unless cancelled.</FooterText>
          <FooterText>
            Questions? Contact us at support@plentyofhearts.com
          </FooterText>
        </Footer>
      </ModalContent>
    </ModalBackdrop>,
    document.body
  );
};
